import React, { useCallback, useMemo, useState } from 'react';
import { Copy, GripVertical, Repeat, Trash2 } from 'lucide-react';
import { LST_CATEGORY_STYLES } from '../../types';
import { PLANNER_HOURS, PLANNER_WEEKDAYS, type PlannerBlock } from '../../services/tplPlannerBlocks';

export type { PlannerBlock } from '../../services/tplPlannerBlocks';

interface TplWeeklyPlannerProps {
  blocks: PlannerBlock[];
  onMoveBlock: (templateId: string, day: number, hour: string) => void;
}

export const TplWeeklyPlanner: React.FC<TplWeeklyPlannerProps> = ({ blocks, onMoveBlock }) => {
  const [dragId, setDragId] = useState<string | null>(null);
  const [localBlocks, setLocalBlocks] = useState<PlannerBlock[]>(blocks);

  React.useEffect(() => {
    setLocalBlocks(blocks);
  }, [blocks]);

  const totalMinutes = useMemo(
    () => localBlocks.reduce((s, b) => s + b.durationMinutes, 0),
    [localBlocks],
  );

  const blocksByCell = useMemo(() => {
    const map = new Map<string, PlannerBlock[]>();
    for (const b of localBlocks) {
      const key = `${b.day}-${b.hour}`;
      const arr = map.get(key) ?? [];
      arr.push(b);
      map.set(key, arr);
    }
    return map;
  }, [localBlocks]);

  const dayLoad = useMemo(() => {
    const loads = new Map<number, number>();
    for (const b of localBlocks) {
      loads.set(b.day, (loads.get(b.day) ?? 0) + b.durationMinutes);
    }
    return PLANNER_WEEKDAYS.map((d) => ({
      day: d.id,
      label: d.label,
      minutes: loads.get(d.id) ?? 0,
    }));
  }, [localBlocks]);

  const maxDayLoad = useMemo(() => Math.max(...dayLoad.map((d) => d.minutes), 1), [dayLoad]);
  const shiftCapacity = 480;
  const weekCapacity = shiftCapacity * PLANNER_WEEKDAYS.length;
  const saturationPct = Math.round((totalMinutes / weekCapacity) * 100);
  const freeMinutes = Math.max(0, weekCapacity - totalMinutes);
  const conflictCells = useMemo(() => {
    let n = 0;
    for (const arr of blocksByCell.values()) {
      if (arr.length > 1) n += 1;
    }
    return n;
  }, [blocksByCell]);

  const handleDrop = useCallback(
    (day: number, hour: string) => {
      if (!dragId) return;
      const block = localBlocks.find((b) => b.id === dragId);
      if (!block) return;
      setLocalBlocks((prev) => prev.map((b) => (b.id === dragId ? { ...b, day, hour } : b)));
      onMoveBlock(block.templateId, day, hour);
      setDragId(null);
    },
    [dragId, localBlocks, onMoveBlock],
  );

  const duplicateBlock = (id: string) => {
    const src = localBlocks.find((b) => b.id === id);
    if (!src) return;
    setLocalBlocks((prev) => [...prev, { ...src, id: `${src.templateId}-copy-${Date.now()}` }]);
  };

  const removeBlock = (id: string) => {
    setLocalBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-[var(--bg-surface)]">
      <header className="shrink-0 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-[13px] font-bold text-slate-800 dark:text-slate-100">Planner Heijunka</h3>
            <p className="text-[11px] text-slate-500">
              Centro de capacidade · nivelamento semanal
            </p>
          </div>
          <div className="flex flex-wrap items-stretch gap-2">
            <CapacityMetric
              label="Saturação"
              value={`${saturationPct}%`}
              tone={saturationPct > 100 ? 'critico' : saturationPct > 85 ? 'atencao' : 'normal'}
            />
            <CapacityMetric
              label="Conflitos"
              value={String(conflictCells)}
              tone={conflictCells > 0 ? 'critico' : 'normal'}
              highlight={conflictCells > 0}
            />
            <CapacityMetric
              label="Capacidade livre"
              value={`${freeMinutes} min`}
              tone={freeMinutes < 60 ? 'atencao' : 'normal'}
            />
          </div>
        </div>

        {/* Heatmap semanal + carga por dia */}
        <div className="mt-3 grid grid-cols-5 gap-1.5">
          {dayLoad.map((d) => {
            const intensity = d.minutes / maxDayLoad;
            const over = d.minutes > shiftCapacity;
            return (
              <div key={d.day} className="rounded-md border border-slate-100 p-2 dark:border-slate-800">
                <p className="text-[9px] font-bold uppercase text-slate-400">{d.label}</p>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                  <div
                    className={`h-full rounded-full ${over ? 'bg-red-500' : 'bg-[#2F6FED]'}`}
                    style={{ width: `${Math.min(100, intensity * 100)}%` }}
                  />
                </div>
                <p className={`mt-1 text-[10px] font-semibold tabular-nums ${over ? 'text-red-600' : 'text-slate-600'}`}>
                  {d.minutes} min
                </p>
              </div>
            );
          })}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="min-w-[720px]">
          <div className="grid grid-cols-[56px_repeat(5,1fr)] gap-px rounded-lg border border-slate-200 bg-slate-200 dark:border-slate-700">
            <div className="bg-slate-50 p-2 text-[9px] font-bold uppercase text-slate-400 dark:bg-slate-900">
              Hora
            </div>
            {PLANNER_WEEKDAYS.map((d) => (
              <div
                key={d.id}
                className="bg-slate-50 p-2 text-center text-[10px] font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                {d.label}
              </div>
            ))}

            {PLANNER_HOURS.map((hour) => (
              <React.Fragment key={hour}>
                <div className="bg-white px-1 py-2 text-[9px] tabular-nums text-slate-500 dark:bg-slate-900/40">
                  {hour}
                </div>
                {PLANNER_WEEKDAYS.map((day) => {
                  const key = `${day.id}-${hour}`;
                  const cellBlocks = blocksByCell.get(key) ?? [];
                  const hasConflict = cellBlocks.length > 1;
                  return (
                    <div
                      key={key}
                      className={`min-h-[52px] bg-white p-1 dark:bg-slate-900/20 ${
                        hasConflict ? 'ring-2 ring-inset ring-red-400/70 bg-red-50/30 dark:bg-red-950/20' : ''
                      }`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleDrop(day.id, hour)}
                    >
                      {cellBlocks.map((b) => {
                        const cat = b.category as keyof typeof LST_CATEGORY_STYLES;
                        const style = LST_CATEGORY_STYLES[cat] ?? LST_CATEGORY_STYLES.apoio;
                        return (
                          <div
                            key={b.id}
                            draggable
                            onDragStart={() => setDragId(b.id)}
                            className={`group mb-1 cursor-grab rounded border border-slate-200 bg-white px-1.5 py-1 shadow-sm active:cursor-grabbing dark:border-slate-700 ${style.border} border-l-4`}
                          >
                            <div className="flex items-start gap-0.5">
                              <GripVertical size={10} className="mt-0.5 shrink-0 text-slate-300" />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-[9px] font-semibold text-slate-800 dark:text-slate-100">
                                  {b.title}
                                </p>
                                <p className="text-[8px] text-slate-400">{b.durationMinutes} min</p>
                              </div>
                              <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                                <button
                                  type="button"
                                  title="Copiar"
                                  onClick={() => duplicateBlock(b.id)}
                                  className="rounded p-0.5 hover:bg-slate-100"
                                >
                                  <Copy size={10} />
                                </button>
                                <button
                                  type="button"
                                  title="Remover"
                                  onClick={() => removeBlock(b.id)}
                                  className="rounded p-0.5 text-red-500 hover:bg-red-50"
                                >
                                  <Trash2 size={10} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <footer className="shrink-0 border-t border-slate-100 px-4 py-2 text-[10px] text-slate-500 dark:border-slate-800">
        <Repeat size={12} className="mr-1 inline" />
        Alterações de horário sincronizam instantaneamente com Meu Padrão (dia).
      </footer>
    </section>
  );
};

function CapacityMetric({
  label,
  value,
  tone,
  highlight = false,
}: {
  label: string;
  value: string;
  tone: 'normal' | 'atencao' | 'critico';
  highlight?: boolean;
}) {
  const styles = {
    normal: 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200',
    atencao: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
    critico: 'border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300',
  };
  return (
    <span
      className={`inline-flex min-w-[88px] flex-col rounded-lg border px-2.5 py-1.5 text-center ${styles[tone]} ${highlight ? 'ring-2 ring-red-400/50' : ''}`}
    >
      <span className="text-[8px] font-bold uppercase tracking-wide opacity-70">{label}</span>
      <span className="text-[14px] font-black tabular-nums leading-tight">{value}</span>
    </span>
  );
}

export default TplWeeklyPlanner;
